import {
  Employee as EmployeeDto,
  EmployeeApplicationAccountResponse as EmployeeApplicationAccountResponseDto,
  EmployeeLeaveResponse as EmployeeLeaveResponseDto,
  EmployeeStatusEnum,
} from '@electricjs/core_entity-client';

import { Employee, EmployeeSource } from '@/types/employees';

export const simpleEmployee: EmployeeDto = {
  id: 'simple-employee-id',
  email: 'simple-user@simple-org.com',
  personal_email: 'simple-user@personal-email.com',
  first_name: 'John',
  last_name: 'Doe',
  job_title: 'Engineer',
  created_at: '2023-05-22',
  updated_at: '2023-05-22',
  status: EmployeeStatusEnum.Active,
  source: EmployeeSource.Manual,
};

export const unknownEmployee1: EmployeeDto = {
  id: 'unknown-employee-id-1',
  email: 'unknown.employee-1@connected-org.com',
  first_name: 'Person',
  last_name: 'One',
  job_title: 'Engineer',
  created_at: '2023-05-22',
  updated_at: '2023-05-22',
  status: EmployeeStatusEnum.Unknown,
  source: EmployeeSource.Hris,
};

export const unknownEmployee2: EmployeeDto = {
  id: 'unknown-employee-id-2',
  email: 'unknown.employee-2@connected-org.com',
  first_name: 'Person',
  last_name: 'Two with very long name',
  job_title: 'Product Manager with very job title',
  created_at: '2023-05-22',
  updated_at: '2023-05-22',
  status: EmployeeStatusEnum.Unknown,
  source: EmployeeSource.Hris,
};

// Pending (status UNKNOWN) employees for the dashboard "Pending employees"
// alert tile and the People page's Pending tab. More than the tile's 10 rows so
// its "N more" footer shows, listed out of start-date order (and one without a
// start date, one without an email) to exercise the tile's sort and gating.
// The first one has an open IT setup request (see requestData.ts).
// Fixed dates (never relative to today) so Storybook/Chromatic snapshots don't
// change from one day to the next.
const PENDING_MOCK_EMPLOYEES: ReadonlyArray<
  Pick<EmployeeDto, 'first_name' | 'last_name' | 'job_title' | 'start_date'> & {
    hasEmail?: boolean;
  }
> = [
  {
    first_name: 'Priya',
    last_name: 'Nair',
    job_title: 'Designer',
    start_date: '2026-11-20',
  },
  {
    first_name: 'Marcus',
    last_name: 'Webb',
    job_title: 'Engineer',
    start_date: '2026-11-06',
  },
  {
    first_name: 'Sofia',
    last_name: 'Alvarez',
    job_title: 'Account Executive',
    start_date: '2026-11-13',
  },
  {
    first_name: 'Noah',
    last_name: 'Kim',
    job_title: 'Data Analyst',
    start_date: '2026-11-03',
  },
  {
    first_name: 'Hannah',
    last_name: 'Berg',
    job_title: 'Recruiter',
    start_date: '2026-12-12',
    hasEmail: false,
  },
  {
    first_name: 'Omar',
    last_name: 'Farouk',
    job_title: 'Support Specialist',
    start_date: '2026-11-27',
  },
  {
    first_name: 'Chloe',
    last_name: 'Martin',
    job_title: 'Product Manager',
    start_date: '2026-12-11',
  },
  {
    first_name: 'Diego',
    last_name: 'Santos',
    job_title: 'Engineer',
    start_date: '2026-11-15',
  },
  {
    first_name: 'Aiko',
    last_name: 'Tanaka',
    job_title: 'Operations Lead',
    start_date: '2026-12-18',
  },
  {
    first_name: 'Liam',
    last_name: "O'Connor",
    job_title: 'Sales Development Rep',
    start_date: '2026-11-22',
  },
  {
    first_name: 'Zara',
    last_name: 'Hussain',
    job_title: 'Marketing Manager',
    start_date: '2026-12-25',
  },
  {
    first_name: 'Ben',
    last_name: 'Carter',
    job_title: 'Contractor',
  },
];

export const pendingMockEmployees: EmployeeDto[] = PENDING_MOCK_EMPLOYEES.map(
  ({ hasEmail = true, ...employee }, index) => ({
    ...employee,
    id: `pending-mock-employee-${index + 1}`,
    ...(hasEmail && {
      email: `pending.mock.${index + 1}@example.com`,
    }),
    created_at: '2026-10-01',
    updated_at: '2026-10-01',
    status: EmployeeStatusEnum.Unknown,
    source: EmployeeSource.Hris,
  })
);

export const createTestEmployee = (
  overrides: Partial<Employee> = {}
): Employee => ({
  id: 'emp-1',
  firstName: 'Mochi',
  lastName: 'Lehmann',
  email: 'mochi.lehmann@gooddoggo.com',
  status: EmployeeStatusEnum.Active,
  source: EmployeeSource.Manual,
  roles: [],
  ...overrides,
});

// Employees for Storefront stories
export const storefrontEmployee1: EmployeeDto = {
  id: 'storefront-emp-1',
  email: 'sarah.johnson@example.com',
  first_name: 'Sarah',
  last_name: 'Johnson',
  job_title: 'Software Engineer',
  created_at: '2024-01-15',
  updated_at: '2024-01-15',
  status: EmployeeStatusEnum.Active,
  source: EmployeeSource.Manual,
  roles: [],
  address: {
    street_1: '123 Main St',
    street_2: 'Apt 4B',
    city: 'San Francisco',
    state: 'CA',
    country: 'US',
    zip_code: '94102',
  },
};

export const storefrontEmployee2: EmployeeDto = {
  id: 'storefront-emp-2',
  email: 'michael.chen@example.com',
  first_name: 'Michael',
  last_name: 'Chen',
  job_title: 'Product Manager',
  created_at: '2024-02-01',
  updated_at: '2024-02-01',
  status: EmployeeStatusEnum.Active,
  source: EmployeeSource.Manual,
  roles: [],
  address: {
    street_1: '456 Market Street',
    street_2: '',
    city: 'New York',
    state: 'NY',
    country: 'US',
    zip_code: '10001',
  },
};

// Offboarding scenario fixtures. Each represents a distinct combination of
// apps / devices / champion roles / open tasks so every step branch in the
// /offboardings/new flow can be exercised without ad-hoc data wiring.
// Handler-side gating (see employeeHandlers, assetHandlers,
// requestData, organizationApplicationData) keys off these ids.
const baseOffboardingFixture = {
  job_title: 'Engineer',
  created_at: '2024-01-01',
  updated_at: '2024-01-01',
  status: EmployeeStatusEnum.Active,
  source: EmployeeSource.Manual,
} as const;

export const noneEmployee: EmployeeDto = {
  ...baseOffboardingFixture,
  id: 'none-employee-id',
  email: 'nora.none@simple-org.com',
  first_name: 'Nora',
  last_name: 'None',
};

export const appsOnlyEmployee: EmployeeDto = {
  ...baseOffboardingFixture,
  id: 'apps-only-employee-id',
  email: 'alex.apps@simple-org.com',
  first_name: 'Alex',
  last_name: 'Apps',
};

export const deviceOnlyEmployee: EmployeeDto = {
  ...baseOffboardingFixture,
  id: 'device-only-employee-id',
  email: 'dana.device@simple-org.com',
  first_name: 'Dana',
  last_name: 'Device',
};

export const champsOnlyEmployee: EmployeeDto = {
  ...baseOffboardingFixture,
  id: 'champs-only-employee-id',
  email: 'casey.champ@simple-org.com',
  first_name: 'Casey',
  last_name: 'Champ',
};

export const tasksOnlyEmployee: EmployeeDto = {
  ...baseOffboardingFixture,
  id: 'tasks-only-employee-id',
  email: 'toby.tasks@simple-org.com',
  first_name: 'Toby',
  last_name: 'Tasks',
};

export const appsChampsEmployee: EmployeeDto = {
  ...baseOffboardingFixture,
  id: 'apps-champs-employee-id',
  email: 'avery.both@simple-org.com',
  first_name: 'Avery',
  last_name: 'Both',
};

/**
 * Per-employee offboarding capabilities. Drives which mock data each
 * employee gets from the handlers (apps, device, champion roles, open tasks)
 * so the /offboardings/new flow can be tested across every combination from
 * a single source of truth.
 */
export type OffboardingCapabilities = {
  hasApps: boolean;
  hasDevice: boolean;
  hasCategoryChampionRole: boolean;
  hasAppChampionRole: boolean;
  hasOpenTasks: boolean;
};

const NO_CAPABILITIES: OffboardingCapabilities = {
  hasApps: false,
  hasDevice: false,
  hasCategoryChampionRole: false,
  hasAppChampionRole: false,
  hasOpenTasks: false,
};

export const offboardingScenarios: ReadonlyArray<{
  employee: EmployeeDto;
  capabilities: OffboardingCapabilities;
}> = [
  // The original test employee — has every offboarding step's data.
  {
    employee: simpleEmployee,
    capabilities: {
      hasApps: true,
      hasDevice: true,
      hasCategoryChampionRole: true,
      hasAppChampionRole: true,
      hasOpenTasks: true,
    },
  },
  { employee: noneEmployee, capabilities: NO_CAPABILITIES },
  {
    employee: appsOnlyEmployee,
    capabilities: { ...NO_CAPABILITIES, hasApps: true },
  },
  {
    employee: deviceOnlyEmployee,
    capabilities: { ...NO_CAPABILITIES, hasDevice: true },
  },
  {
    employee: champsOnlyEmployee,
    capabilities: { ...NO_CAPABILITIES, hasCategoryChampionRole: true },
  },
  {
    employee: tasksOnlyEmployee,
    capabilities: { ...NO_CAPABILITIES, hasOpenTasks: true },
  },
  {
    employee: appsChampsEmployee,
    capabilities: {
      ...NO_CAPABILITIES,
      hasApps: true,
      hasCategoryChampionRole: true,
      hasAppChampionRole: true,
    },
  },
];

const idsWith = (predicate: (c: OffboardingCapabilities) => boolean) =>
  new Set(
    offboardingScenarios
      .filter(s => predicate(s.capabilities))
      .map(s => s.employee.id)
  );

export const employeeIdsWithApps = idsWith(c => c.hasApps);
export const employeeIdsWithDevice = idsWith(c => c.hasDevice);
export const employeeIdsWithCategoryChampionRole = idsWith(
  c => c.hasCategoryChampionRole
);
export const employeeIdsWithAppChampionRole = idsWith(
  c => c.hasAppChampionRole
);
export const employeeIdsWithOpenTasks = idsWith(c => c.hasOpenTasks);

export const offboardingScenarioEmployeesById: Record<string, EmployeeDto> =
  Object.fromEntries(
    offboardingScenarios.map(s => [s.employee.id, s.employee])
  );

// Zoom and Teams match the ids in `simpleGroupApplicationList` so the apps
// actually render wherever an employee's accounts are matched against them.
const ZOOM_APPLICATION_ID = 'aaeea7bc-054e-11ee-be56-0242ac120002';
const TEAMS_APPLICATION_ID = '13e20646-1322-4493-8417-8993e77332cc';

export const createEmployeeApplicationAccounts = (
  organizationId: string,
  employeeId: string
): EmployeeApplicationAccountResponseDto[] =>
  [
    { id: 'eaa-zoom', application_id: ZOOM_APPLICATION_ID },
    { id: 'eaa-teams', application_id: TEAMS_APPLICATION_ID },
  ].map(account => ({
    ...account,
    organization_id: organizationId,
    employee_id: employeeId,
    created_at: '2024-01-01T00:00:00.000Z',
    updated_at: '2024-01-01T00:00:00.000Z',
  }));

// In-memory leaves so a created/edited/canceled leave shows up on the next GET.
export const mockEmployeeLeaves: EmployeeLeaveResponseDto[] = [];
